/**
 * Non-solid trigger volumes (start, finish, checkpoints).
 *
 * Triggers never block motion. They are tested against the player's actual
 * capsule along the *resolved* path of each tick (not just the end position),
 * so a fast player cannot skip a thin finish line. Path samples are spaced at
 * most radius/2 apart; since each box is effectively inflated by the radius,
 * no box can be stepped over between samples.
 */
import { Vec3 } from '../core/Vec3';
import type { PathPoint } from '../movement/ContactResolver';
import type { TriggerDef } from './LevelData';

export interface TriggerEvent {
  trigger: TriggerDef;
  type: 'enter' | 'exit';
  /** Tick-time fraction [0, 1] of the event within the tick. */
  time: number;
}

/** Squared distance from point p to an AABB. */
function pointBoxDistSq(px: number, py: number, pz: number, t: TriggerDef): number {
  let d = 0;
  for (let i = 0; i < 3; i++) {
    const p = i === 0 ? px : i === 1 ? py : pz;
    const lo = t.center[i]! - t.halfExtents[i]!;
    const hi = t.center[i]! + t.halfExtents[i]!;
    const e = p < lo ? lo - p : p > hi ? p - hi : 0;
    d += e * e;
  }
  return d;
}

const KIND_ORDER: Record<TriggerDef['kind'], number> = { start: 0, checkpoint: 1, finish: 2 };

export class TriggerSystem {
  private readonly inside: boolean[];
  private readonly events: TriggerEvent[] = [];
  private readonly a = new Vec3();
  private readonly b = new Vec3();
  private readonly s = new Vec3();

  constructor(
    readonly triggers: readonly TriggerDef[],
    private readonly radius: number,
    private readonly segmentLength: number,
  ) {
    this.inside = triggers.map(() => false);
  }

  /** Re-evaluates occupancy without emitting events (e.g. after respawn). */
  resetOccupancy(feet: Readonly<Vec3>, up: Readonly<Vec3>): void {
    for (let i = 0; i < this.triggers.length; i++) this.inside[i] = this.capsuleOverlaps(feet, up, this.triggers[i]!);
  }

  isInside(kind: TriggerDef['kind']): boolean {
    return this.triggers.some((t, i) => t.kind === kind && this.inside[i]);
  }

  /** Processes one tick's path. Returned array is reused; events are time-ordered. */
  update(path: readonly PathPoint[], pathCount: number, up: Readonly<Vec3>): readonly TriggerEvent[] {
    this.events.length = 0;
    if (pathCount === 0) return this.events;
    const step = this.radius * 0.5;
    for (let ti = 0; ti < this.triggers.length; ti++) {
      const trig = this.triggers[ti]!;
      let wasInside = this.inside[ti]!;
      const segments = Math.max(1, pathCount - 1);
      for (let pi = 0; pi < segments; pi++) {
        const p0 = path[pi]!;
        const p1 = path[Math.min(pi + 1, pathCount - 1)]!;
        const len = p0.position.distanceTo(p1.position);
        const samples = Math.max(1, Math.ceil(len / step));
        for (let k = pi === 0 ? 0 : 1; k <= samples; k++) {
          const f = k / samples;
          this.s.lerpVectors(p0.position, p1.position, f);
          const now = this.capsuleOverlaps(this.s, up, trig);
          if (now !== wasInside) {
            this.events.push({ trigger: trig, type: now ? 'enter' : 'exit', time: p0.time + (p1.time - p0.time) * f });
            wasInside = now;
          }
        }
      }
      this.inside[ti] = wasInside;
    }
    // Equal times (coincident trigger faces) resolve by a fixed rule, never by
    // the order triggers are listed in the level: start, checkpoint, finish, then id.
    this.events.sort((x, y) => x.time - y.time || KIND_ORDER[x.trigger.kind] - KIND_ORDER[y.trigger.kind] || (x.trigger.id < y.trigger.id ? -1 : x.trigger.id > y.trigger.id ? 1 : 0));
    return this.events;
  }

  /** Capsule (feet position, oriented along up) vs AABB overlap. */
  capsuleOverlaps(feet: Readonly<Vec3>, up: Readonly<Vec3>, t: TriggerDef): boolean {
    const r = this.radius;
    this.a.copy(feet).addScaled(up, r);
    this.b.copy(feet).addScaled(up, r + this.segmentLength);
    // Distance from a segment to a box is convex along the segment: ternary search.
    let lo = 0;
    let hi = 1;
    const f = (u: number): number =>
      pointBoxDistSq(
        this.a.x + (this.b.x - this.a.x) * u,
        this.a.y + (this.b.y - this.a.y) * u,
        this.a.z + (this.b.z - this.a.z) * u,
        t,
      );
    for (let i = 0; i < 24; i++) {
      const m1 = lo + (hi - lo) / 3;
      const m2 = hi - (hi - lo) / 3;
      if (f(m1) <= f(m2)) hi = m2;
      else lo = m1;
    }
    return Math.min(f((lo + hi) / 2), f(0), f(1)) <= r * r;
  }
}
